import React, { useEffect, useState } from "react";
import { apiRequest } from "../../api";

export default function PlatformStripeBilling() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStripeAuthority();
  }, []);

  async function loadStripeAuthority() {
    setLoading(true);
    setErr("");

    try {
      const result = await apiRequest("/platform/billing/stripe-authority");
      setData(result);
    } catch (error) {
      setErr(error.message || "Failed to load Stripe Billing Authority.");
    } finally {
      setLoading(false);
    }
  }

  const sectionCard = {
    background: "#13293d",
    borderRadius: 12,
    padding: 20,
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: 20,
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
    marginTop: 16,
  };

  const metricCard = {
    background: "#0b1b2b",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: 16,
  };

  const tenants = data?.tenants || [];
  const summary = data?.summary || {};

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: "0 0 8px" }}>Stripe Billing Authority</h1>
        <div style={{ color: "#9fb0c3", fontSize: 16 }}>
          Real Stripe membership visibility separated from manual platform billing controls.
        </div>
      </div>

      {err && (
        <div style={{ ...sectionCard, color: "#fecaca" }}>
          {err}
        </div>
      )}

      {loading && <div style={sectionCard}>Loading Stripe billing authority...</div>}

      {!loading && data && (
        <>
          <div style={sectionCard}>
            <h2 style={{ marginTop: 0 }}>Stripe Summary</h2>
            <div style={{ color: "#9fb0c3" }}>
              Shows whether tenants are connected to Stripe subscription infrastructure.
            </div>

            <div style={grid}>
              <Metric label="Total Tenants" value={summary.total_tenants} />
              <Metric label="Stripe Connected" value={summary.stripe_connected} />
              <Metric label="Stripe Missing" value={summary.stripe_missing} />
              <Metric label="Not Required" value={summary.stripe_not_required} />
              <Metric label="Canceling" value={summary.cancel_at_period_end} />
            </div>
          </div>

          <div style={sectionCard}>
            <h2 style={{ marginTop: 0 }}>Tenant Stripe Memberships</h2>
            <div style={{ color: "#9fb0c3", marginBottom: 16 }}>
              Stripe truth stored in JobTelesis. Manual free/trial overrides belong on Billing Control.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 18 }}>
              {tenants.map((t) => (
                <div key={t.id} style={metricCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 18 }}>{t.name}</div>
                      <div style={{ color: "#9fb0c3", fontSize: 12, marginTop: 4 }}>
                        {t.subdomain} · Tenant ID {t.id}
                      </div>
                    </div>

                    <span
                      style={{
                        padding: "5px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800,
                        background: readinessColor(t.stripe_readiness),
                        color: "#ffffff",
                        height: "fit-content",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {readinessLabel(t.stripe_readiness, t)}
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
                    {(t.health_signals || []).map((signal, idx) => (
                      <span
                        key={idx}
                        title={signal.meaning}
                        style={{
                          padding: "5px 9px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 800,
                          background: signalColor(signal.color),
                          color: "#ffffff",
                        }}
                      >
                        {signal.label}
                      </span>
                    ))}
                  </div>

                  <div style={{ ...grid, gridTemplateColumns: "1fr 1fr", marginTop: 16 }}>
                    <Mini label="Tenant Type" value={title(t.tenant_type || "standard")} />
                    <Mini label="Plan" value={title(t.subscription_plan)} />
                    <Mini label="Billing Mode" value={title(t.billing_mode)} />
                    <Mini label="Status" value={title(t.subscription_status)} />
                    <Mini label="Stripe Connected" value={t.stripe_connected ? "Yes" : "No"} />
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Field label="Customer ID" value={t.stripe_customer_id || "None"} />
                    <Field label="Subscription ID" value={t.stripe_subscription_id || "None"} />
                    <Field label="Current Period End" value={t.stripe_current_period_end || "None"} />
                    <Field label="Cancel At Period End" value={t.stripe_cancel_at_period_end ? "Yes" : "No"} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  function Metric({ label, value }) {
    return (
      <div style={metricCard}>
        <div style={{ color: "#9fb0c3", fontSize: 12, marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800 }}>{value ?? 0}</div>
      </div>
    );
  }
}

function Mini({ label, value }) {
  return (
    <div style={{ background: "#13293d", borderRadius: 10, padding: 12 }}>
      <div style={{ color: "#9fb0c3", fontSize: 11, marginBottom: 6 }}>{label}</div>
      <div style={{ fontWeight: 800 }}>{value || "—"}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "10px 0" }}>
      <div style={{ color: "#9fb0c3", fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 13, wordBreak: "break-all" }}>{value}</div>
    </div>
  );
}

function readinessLabel(value, tenant = {}) {
  if (tenant.tenant_type === "beta" || tenant.billing_mode === "beta") return "Beta Tenant";
  if (value === "connected") return "Stripe Connected";
  if (value === "missing") return "Stripe Missing";
  return "Not Required";
}

function readinessColor(value) {
  if (value === "connected") return "#166534";
  if (value === "missing") return "#991b1b";
  return "#334155";
}

function title(value) {
  return String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}


function signalColor(color) {
  const colors = {
    green: "#166534",
    blue: "#1d4ed8",
    yellow: "#a16207",
    orange: "#c2410c",
    red: "#991b1b",
  };

  return colors[color] || "#334155";
}
